/**
 * DS-01 Discourse scaffolding (INV-CH-DS-01; kept, sentence counts from the new splitter C-SPL).
 *
 * Events: SCAFFOLD_RE hits (a sentence-initial connective such as "Moreover," or "In conclusion")
 * per prose sentence. The evidence span covers the connective only, not the boundary before it.
 */
import {
	Tally,
	compiledEnglish,
	line,
	nativeMeasure,
	proseBlocks,
	rateDef,
	sentenceExposure,
} from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

export const ds01Def = rateDef({
	id: 'DS-01',
	name: 'Discourse scaffolding',
	family: 'FORMULA',
	features: [{ id: 'scaffold', label: 'Sentence-initial connective' }],
	oneWay: true,
	minExposure: 20,
	exposureUnit: 'sentences',
	lang: 'native',
	ramp: [
		[0, 10],
		[0.05, 25],
		[0.1, 45],
		[0.18, 68],
		[0.3, 90],
	],
	unitScale: 1,
	unitLabel: 'scaffolded openings per sentence',
	phi0: 1.5,
	weight: 1.4,
	nd: 0.5,
	changes: ['C-SPL'],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const lex = compiledEnglish();
	const k = new Float64Array(doc.paragraphs.count);
	const e = sentenceExposure(doc);
	const ev = makeEvidence();
	const hits = new Tally();
	for (const b of proseBlocks(doc)) {
		lex.scaffoldRe.scanBlock(b.text, b.base, (s, _en, _i, m) => {
			const lead = (m[1] as string).length;
			const word = m[2] as string;
			k[b.p] = (k[b.p] as number) + 1;
			ev.add(s + lead, s + lead + word.length, 0, 1);
			hits.add(word.toLowerCase());
		});
	}
	const summary = hits.top(12).map(([w, n]) => line(w, `${n}× sentence-initial`));
	return { k, e, evidence: ev, summary };
}

export const ds01: ChannelImpl = rateChannel(ds01Def, nativeMeasure(measure));
export { ds01Def as def, ds01 as impl };
